import React, { useRef, useState, useEffect } from 'react';
import Cropper from 'react-cropper';
import { TabContent, TabPane, Nav, NavItem, NavLink } from 'reactstrap';
import { useTranslation } from '@blocs.i18n';
import Modal from '@minecraft.modal';
import { gqlMedia } from '@minecraft.graphql-types';
import ImageThumbnailCropper from './ImageThumbnailCropper';
import ImageCropper from './ImageCropper';

export type ImageCropperModalProps = {
  isOpen: boolean;
  handleClose: () => void;
  handleSave?: (data: Cropper) => void;
  handleThumbnailSave?: (data: Cropper) => void;
  media: Pick<gqlMedia, 'fileKey' | 'transformation'>;
  disableSubmit?: boolean;
};

export const ImageCropperModal = ({
  isOpen,
  handleClose,
  handleSave,
  media,
  disableSubmit = false,
  handleThumbnailSave,
}: ImageCropperModalProps) => {
  const { t } = useTranslation();
  const cropperRef = useRef<{ cropper: Cropper }>(null);
  const thumbCropRef = useRef<{ cropper: Cropper }>(null);
  const [activeTab, setActiveTab] = useState('photo');
  const [init, setInit] = useState(false);
  const [isPhotoChanged, setIsPhotoChanged] = useState<boolean>(false);
  const [isThumbnailChanged, setIsThumbnailChanged] = useState<boolean>(false);

  useEffect(() => {
    if (!isOpen) {
      setActiveTab('photo');
      setInit(false);
    }
  }, [isOpen]);

  const toggleTab = (tab): void => {
    if (tab === 'thumbnail') {
      if (!init) setInit(true);
    }

    setActiveTab(tab);
  };

  const handleSubmit = (): void => {
    if (isPhotoChanged && handleSave) {
      handleSave(cropperRef?.current?.cropper);
    }

    if (isThumbnailChanged && handleThumbnailSave) {
      handleThumbnailSave(thumbCropRef?.current?.cropper);
    }
  };
  const submitDisabled =
    disableSubmit || (activeTab === 'photo' && !isPhotoChanged) || (activeTab === 'thumbnail' && !isThumbnailChanged);

  return (
    <Modal
      isOpen={isOpen}
      title={t('common:profile.button.editPhotoThumbnail')}
      modalSize="medium"
      cancelLabel={t('common:button.cancel')}
      submitLabel={t('common:button.save')}
      submitDisabled={submitDisabled}
      handleToggleClick={handleClose}
      handleSubmitClick={handleSubmit}
      data-testid="image-cropper-modal"
    >
      <Nav tabs role="tablist">
        <NavItem>
          <NavLink
            role="tab"
            aria-selected={activeTab === 'photo'}
            className={activeTab === 'photo' ? 'active' : ''}
            onClick={(): void => toggleTab('photo')}
            id="photo-tab-button"
            aria-controls="photo-tab-pane"
          >
            {t('common:mediaType.photo')}
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            role="tab"
            aria-selected={activeTab === 'thumbnail'}
            className={activeTab === 'thumbnail' ? 'active' : ''}
            onClick={(): void => toggleTab('thumbnail')}
            id="thumbnail-tab-button"
            aria-controls="thumbnail-tab-pane"
          >
            {t('common:imageCropper.thumbnail')}
          </NavLink>
        </NavItem>
      </Nav>

      <TabContent activeTab={activeTab}>
        <TabPane
          className="cn_mt-4"
          tabId="photo"
          id="photo-tab-pane"
          role="tabpanel"
          aria-labelledby="photo-tab-button"
        >
          <ImageCropper
            media={media}
            alt="image"
            sideViewTools
            showCropTools
            customRef={cropperRef}
            isChanged={isPhotoChanged}
            setIsChanged={setIsPhotoChanged}
          />
        </TabPane>

        <TabPane
          className="cn_mt-4"
          tabId="thumbnail"
          id="thumbnail-tab-pane"
          role="tabpanel"
          aria-labelledby="thumbnail-tab-button"
        >
          {init && (
            <ImageThumbnailCropper
              media={media}
              alt="image"
              sideViewTools
              showCropTools
              customRef={thumbCropRef}
              isChanged={isThumbnailChanged}
              setIsChanged={setIsThumbnailChanged}
            />
          )}
        </TabPane>
      </TabContent>
    </Modal>
  );
};
